import React, { FC, UIEvent, useCallback } from 'react';
import { PerfectScrollbar } from '@src/components/PerfectScrollbar';
import { TableFilter } from '@src/components/TableFilter';
import { Loader } from '@src/components/Loader';
import { 
  TABLE_STYLES, 
  getRowClass, 
  getTdClassWithColor, 
  unreadDotStyle,
  getMessageTextStyle,
  getCheckboxStyle 
} from './tableStyles';
import Checkbox from '@mui/material/Checkbox';

interface TableCompactProps {
  rows: any[];
  isLoading: boolean;
  isFetchingMore?: boolean;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  selectedRowId?: string | null;
  checkedRows?: Array<{ conversationId: string; interlocutorId: string; senderId: string }>;
  onCheckboxChange?: (info: { conversationId: string; interlocutorId: string; senderId: string }, checked: boolean) => void;
  getFilterOptions: (field: string) => Array<{ value: string; label: string; selected: boolean; }>;
  onFilterChange: (field: string, selected: string[]) => void;
  onSortChange: (sortBy: string, direction: 'asc' | 'desc') => void;
  onRowClick: (row: any) => void;
  renderIPRCell: (hasIPR: boolean, iprCount: number) => JSX.Element;
  onToggleFavorite: (conversationId: string) => void;
  onLoadMore?: () => void;
}

export const TableCompact: FC<TableCompactProps> = ({
  rows,
  isLoading,
  isFetchingMore,
  sortBy,
  sortDirection,
  selectedRowId,
  checkedRows: selectedRows = [],
  onCheckboxChange,
  getFilterOptions,
  onFilterChange,
  onSortChange,
  onRowClick,
  renderIPRCell,
  onToggleFavorite,
  onLoadMore,
}) => {
  const messageTdStyle = { ...TABLE_STYLES.tdClass, maxWidth: 200 };
  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (!onLoadMore) return;
      const target = event.currentTarget;
      const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
      if (distanceToBottom < 200) {
        onLoadMore();
      }
    },
    [onLoadMore],
  );

  return (
    <PerfectScrollbar 
      style={TABLE_STYLES.scrollContainerStyle}               
      options={{
        suppressScrollX: true,
        wheelPropagation: false,
        minScrollbarLength: 20,
      }}
      onScroll={handleScroll}
    >
      <div style={TABLE_STYLES.scrollContentStyle}>
        <table style={TABLE_STYLES.tableClass}>
          <thead>
            <tr style={TABLE_STYLES.headerRowClass}>
              <th style={TABLE_STYLES.thClass}>
                <div style={TABLE_STYLES.headerWithFilterClass}>Select</div>
              </th>
              <th style={TABLE_STYLES.thClass}>
                <div style={TABLE_STYLES.headerWithFilterClass}>Favorite</div>
              </th>
              <th style={TABLE_STYLES.thClass}>
                <div style={TABLE_STYLES.headerWithFilterClass}>
                  Date
                  <div style={TABLE_STYLES.sortControlsStyle}>
                    {sortBy === 'date' ? (
                      sortDirection === 'asc' ? 
                        <span style={TABLE_STYLES.sortIconClass} onClick={() => onSortChange('date', 'desc')}>↑</span> :
                        <span style={TABLE_STYLES.sortIconClass} onClick={() => onSortChange('date', 'asc')}>↓</span>
                    ) : null }
                    {sortBy !== 'date' ? (
                      <span style={TABLE_STYLES.sortIconInactiveStyle} onClick={() => onSortChange('date', 'desc')}>↕</span>
                    ) : null}
                  </div>
                </div>
              </th>
              <th style={TABLE_STYLES.thClass}>
                <div style={TABLE_STYLES.headerWithFilterClass}>
                  Username
                  <TableFilter
                    type="filter"
                    options={getFilterOptions('username')}
                    onFilterChange={(selected) => onFilterChange('username', selected)}
                  />
                </div>
              </th>
              <th style={TABLE_STYLES.thClass}>
                <div style={TABLE_STYLES.headerWithFilterClass}>
                  Rights Agreed
                  <TableFilter
                    type="filter"
                    options={getFilterOptions('rightsAgreed')}
                    onFilterChange={(selected) => onFilterChange('rightsAgreed', selected)}
                  />
                </div>
              </th>
              <th style={TABLE_STYLES.thClass}>Last Message</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} style={TABLE_STYLES.emptyRowStyle}>
                  {isLoading ? (
                    <div style={TABLE_STYLES.loadingContainerStyle}>
                      <Loader size={20} />
                      <span>Loading conversations...</span>
                    </div>
                  ) : (
                    <div style={TABLE_STYLES.emptyStateContainerStyle}>
                      <div style={TABLE_STYLES.emptyStateTitleStyle}>
                        No conversations found
                      </div>
                      <div style={TABLE_STYLES.emptyStateDescriptionStyle}>
                        Start messaging on your social platforms to see conversations here
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const dynamicTdClass = getTdClassWithColor(row.conversationType, row.hasUnread);
                const dynamicRowClass = getRowClass(row.id, selectedRowId, row.hasUnread);
                return (
                  <tr
                    key={row.id}
                    style={dynamicRowClass}
                    onClick={() => onRowClick(row)}
                  >
                    <td style={{ ...dynamicTdClass, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                      <Checkbox
                        checked={!!(selectedRows && selectedRows.some(r => r.conversationId === row.id))}
                        onChange={e =>
                          onCheckboxChange &&
                          onCheckboxChange(
                            { conversationId: row.id, interlocutorId: row.interlocutorId, senderId: row.targetId },
                            e.target.checked
                          )
                        }
                        size="small"
                        sx={getCheckboxStyle(row.conversationType)}
                      />
                    </td>
                    <td style={{ ...dynamicTdClass, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                      <span 
                        style={{ 
                          cursor: 'pointer', 
                          fontSize: '18px',
                          color: row.isFavorite ? '#FFD700' : '#666',
                          transition: 'color 0.2s ease'
                        }}
                        onClick={() => onToggleFavorite(row.id)}
                        title={row.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        {row.isFavorite ? '★' : '☆'}
                      </span>
                    </td>
                    <td style={dynamicTdClass}>
                      {row.date}
                    </td>
                    <td style={dynamicTdClass}>
                      <strong>{row.username}</strong>
                    </td>
                    <td style={dynamicTdClass}>
                      {renderIPRCell(row.hasIPR, row.iprCount)}
                    </td>
                    <td style={messageTdStyle}>
                      <div style={TABLE_STYLES.messageContainerStyle}>
                        {row.hasUnread ? (
                          <div style={unreadDotStyle(row.hasUnread, row.conversationType === 'stored')} />
                        ) : null }
                        <span style={getMessageTextStyle(row.conversationType)}>
                          {row.message}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
            {isFetchingMore ? (
              <tr>
                <td colSpan={6} style={TABLE_STYLES.loadMoreRowStyle}>
                  <div style={TABLE_STYLES.loadingContainerStyle}>
                    <Loader size={16} />
                    <span>Loading more conversations...</span>
                  </div>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </PerfectScrollbar>
  );
};
